/* eslint-disable react/jsx-indent */
import {
  Paper,
  Typography,
  Chip,
  Divider,
  IconButton,
  Stack,
  Button,
  Tooltip,
  useMediaQuery,
} from '@mui/material';
import { Box } from '@mui/system';
import moment from 'moment';
import { useState, useEffect } from 'react';
import Details, { mapByRefValue } from '../components/Details/Details';
import {
  ApiUsability,
  AppleTitleNamingKeys,
  ISeason,
  ISeasonalTemp,
  Season,
} from '../lib/generalTypes';
import {
  formatMediaDataOmeka,
  mergeObjectWithSameKeys,
  seasonsBuilder,
  sortCustomOrderObject,
  transformGreekToEnglish,
} from '../lib/helperFuncs';
import { generalMapping } from '../lib/mappings/generalMapping';
import GMapsPlaceMarker from '../components/Maps/GMapsPlaceMarker';
import { MarkerProps } from '@react-google-maps/api';
import { useTranslation } from 'react-i18next';
import LinkButton from '../components/Buttons/LinkButton';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import FavoriteIcon from '@mui/icons-material/Favorite';
import { useSnackbar } from 'notistack';
import { useLocation, useParams } from 'react-router-dom';
import HttpService from '../services/HttpService';
import UserService from '../services/UserService';
import LocationDetailsSkeleton from '../components/Skeletons/LocationDetailsSkeleton';
import { VisibilityEnum } from '../components/dictionaries/LocationVisibility';
import { CloudIcon, SunIcon } from '../assets';
import DetailsWrapper from '../components/Details/DetailsWrapper';
import DetailsListWrapper from '../components/Details/DetailsListWrapper';
import CarouselContainerForDetails from '../components/Carousel/CarouselContainerForDetails';
import Attachments from '../components/Attachments/Attachments';
import { IMediaAttachments } from './ΟΑ/Tekmirio/types';
import {
  _contactDetails,
  _usefulInfo,
  _locationInfo,
} from '../lib/static/locations/details';
import { IntExt, Permissions } from '../lib/permissions';
import DialogForm from '../components/Dialog/DialogForm';
import { initialValues } from '../lib/static/professionals/initialValues';
import { validationSchemaCommForm } from '../lib/static/professionals/validationSchema';
import { communicationFields } from '../lib/static/professionals/formFields';
import BreadcrumbContext from '../components/context/BreadcrumbContext';
import { useContext } from 'react';
import Theme from '../Themes/main.theme';
import i18n from '../i18n';
import EmptyPage from '../components/EmptyPage/EmptyPage';
import { FormikContextType, FormikValues } from 'formik';
import { useApiState } from '../hooks/useApiState';
import { useFromAccessLogic } from '../hooks/useFromAccessLogic';
import TrainIcon from '@mui/icons-material/Train';
import LocalAirportIcon from '@mui/icons-material/LocalAirport';
import DirectionsBusFilledIcon from '@mui/icons-material/DirectionsBusFilled';
import DirectionsBoatIcon from '@mui/icons-material/DirectionsBoat';

function LocationDetails() {
  const [temps, setTemps] = useState<ISeasonalTemp[]>([]);
  const [mappedMetadata, setMappedMetadata] = useState<any>({});
  const [media, setMedia] = useState<any[]>([]);
  const [contactDetails, setContactDetails] = useState<any>(_contactDetails);
  const [usefulInfo, setUsefulInfo] = useState<any>(_usefulInfo);
  const [locationInfo, setLocationInfo] = useState<any>(_locationInfo);
  const [location, setLocation] = useState<any>(undefined);
  const [title, setTitle] = useState<string>();
  const [isFavorite, setIsFavorite] = useState<boolean>(false);
  const [favoriteError, setFavoriteError] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const { enqueueSnackbar } = useSnackbar();
  const [visibility, setVisibility] = useState<VisibilityEnum>();
  const [relativeProfessionals, setRelativeProfessionals] = useState();
  const [relativeMovies, setRelativeMovies] = useState();
  const [relativeProjects, setRelativeProjects] = useState<any[]>();
  const [attachments, setAttachments] = useState<IMediaAttachments[]>([]);
  const [locationDistances, setLocationDistances] = useState<any[]>([]);
  const [open, setOpen] = useState<boolean>(false);
  const [isApplicant, setIsApplicant] = useState<boolean>(false);
  const [errorMsg, setErrorMsg] = useState<string | any>();
  const [isLoadingContact, setIsLoadingContact] = useState<boolean>(true);

  let NUMBER_OF_SKELETON_BOXES = 4;
  const matchesLGbreakpoint = useMediaQuery('(max-width:1550px)');
  const matchesMDbreakpoint = useMediaQuery('(max-width:1024px)');
  const matchesSMbreakpoint = useMediaQuery('(max-width:768px)');
  const matchesXSbreakpoint = useMediaQuery('(max-width:480px)');

  if (matchesXSbreakpoint) {
    NUMBER_OF_SKELETON_BOXES = 1;
  } else if (matchesSMbreakpoint && matchesMDbreakpoint) {
    NUMBER_OF_SKELETON_BOXES = 2;
  } else if (matchesMDbreakpoint && matchesLGbreakpoint) {
    NUMBER_OF_SKELETON_BOXES = 3;
  } else {
    NUMBER_OF_SKELETON_BOXES = 4;
  }

  const { id } = useParams();
  const { setName } = useContext(BreadcrumbContext.BreadcrumbProvider.context);

  const { t } = useTranslation();
  const { pathname } = useLocation();

  const { apiEnabled, loadingApi } = useApiState(
    ApiUsability.Google_Maps,
    true
  );

  const is_external = UserService.hasRole([IntExt.External]);

  const { availableForms, isLoading: isFormPublishedLoading } =
    useFromAccessLogic(AppleTitleNamingKeys.ContactLocation);

  const {
    availableForms: availableFormsShooting,
    isLoading: isFormPublishedShootingLoading,
  } = useFromAccessLogic(AppleTitleNamingKeys.FilmingRequest);

  const handleClickOpen = () => {
    setOpen(true);
  };

  const matchSeasonRefToLabel = (refValue: Season) => {
    switch (refValue) {
    case Season.Winter:
      return t('Χειμώνας');
    case Season.Spring:
      return t('Άνοιξη');
    case Season.Summer:
      return t('Καλοκαίρι');
    case Season.Fall:
      return t('Φθινόπωρο');
    default:
      return;
    }
  };

  const formatStrippedDetailsRows = (temps: ISeasonalTemp[]): any[] => {
    const rows = Object.entries(temps).map((x: any) => {
      return {
        name: matchSeasonRefToLabel(x[0]),
        value: [
          `${moment(x[1].dateStart).format('DD/MM/YYYY')} - ${moment(
            x[1].dateEnd
          ).format('DD/MM/YYYY')} `,
          `${x[1].avgTemp} °C`,
        ],
      };
    });
    rows.unshift({
      name: t('Εποχή') as string,
      value: [t('Μήνες'), t('Μέση Θερμοκρασία')],
    });
    return rows;
  };

  const fetchData = async () => {
    try {
      setName('');
      const resp = await HttpService.getAxiosClient().get(
        `${process.env.REACT_APP_BACKEND_URL}/locations/${id}`
      );
      const data = resp.data;

      if (resp.status !== 200) {
        setErrorMsg(t('Η σελίδα που ζητήσατε δεν βρέθηκε.'));
        setIsLoading(false);
      }

      setIsApplicant(resp.data.is_applicant_user);

      setLocationDistances([
        ...(data.distances.airport_distance
          ? [
            {
              name: (
                <span>
                  <LocalAirportIcon sx={{ mr: 2, mb: -0.7 }} />
                  {i18n.language === 'en'
                    ? transformGreekToEnglish(`${data.distances.airport}`)
                    : data.distances.airport}
                </span>
              ),
              value: [
                `${
                  data.distances.airport_distance
                    ? data.distances.airport_distance
                    : '-'
                } km`,
              ],
            },
          ]
          : []),
        ...(data.distances.port_distance
          ? [
            {
              name: (
                <span>
                  <DirectionsBoatIcon sx={{ mr: 2, mb: -0.7 }} />
                  {i18n.language === 'en'
                    ? transformGreekToEnglish(`${data.distances.port}`)
                    : data.distances.port}
                </span>
              ),
              value: [
                `${
                  data.distances.port_distance
                    ? data.distances.port_distance
                    : '-'
                } km`,
              ],
            },
          ]
          : []),
        ...(data.distances.bus_distance
          ? [
            {
              name: (
                <span>
                  <DirectionsBusFilledIcon sx={{ mr: 2, mb: -0.7 }} />
                  {i18n.language === 'en'
                    ? transformGreekToEnglish(`Bus ${data.distances.bus}`)
                    : `ΚΤΕΛ ${data.distances.bus}`}
                </span>
              ),
              value: [
                `${
                  data.distances.bus_distance
                    ? data.distances.bus_distance
                    : '-'
                } km`,
              ],
            },
          ]
          : []),
        ...(data.distances.train_distance
          ? [
            {
              name: (
                <span>
                  <TrainIcon sx={{ mr: 2, mb: -0.7 }} />
                  {i18n.language === 'en'
                    ? transformGreekToEnglish(`Train ${data.distances.train}`)
                    : `Τρένο ${data.distances.train}`}
                </span>
              ),
              value: [
                `${
                  data.distances.train_distance
                    ? data.distances.train_distance
                    : '-'
                } km`,
              ],
            },
          ]
          : []),
      ]);

      setIsFavorite(data?.item?.is_favorite);
      setTitle(
        i18n.language === 'en'
          ? transformGreekToEnglish(`${data?.item?.['o:title']}`)
          : data?.item?.['o:title']
      );

      setVisibility(
        data?.item?.['locations:locationsVisibility']?.[0]?.['@value']
      );

      setName(
        i18n.language === 'en'
          ? transformGreekToEnglish(`${data?.item?.['o:title']}`)
          : data?.item?.['o:title']
      );

      document.title =
        i18n.language === 'en'
          ? transformGreekToEnglish(`${data?.item?.['o:title']}`)
          : data?.item?.['o:title'];

      const omekaLocation: MarkerProps = {
        position: {
          lat: Number(data?.item?.['locations:latitude']?.[0]?.['@value']),
          lng: Number(data?.item?.['locations:longitude']?.[0]?.['@value']),
        },
      };
      setLocation(omekaLocation.position);
      const estimatedYear: number = moment().subtract(2, 'years').year();
      const seasonObj: ISeason[] = seasonsBuilder(estimatedYear);
      const detailsRows =
        data?.weather && mergeObjectWithSameKeys(data?.weather, seasonObj[0]);

      const order = ['winter', 'spring', 'summer', 'fall'];
      setTemps([sortCustomOrderObject(order, detailsRows)]);

      const _mappedMetadata = generalMapping(data.item);

      setMappedMetadata(_mappedMetadata);
      setLocationInfo(
        mapByRefValue(_mappedMetadata, _locationInfo(_mappedMetadata)).map(
          (x) => {
            return {
              ...x,
              name: t(`${x.name}`),
              ...(x.refValue === 'address' && {
                value:
                  i18n.language === 'en'
                    ? transformGreekToEnglish(`${x.value}`)
                    : x.value,
              }),
              ...(x.refValue === 'region' && { value: t(x.value) }),
            };
          }
        )
      );
      setRelativeProfessionals(_mappedMetadata.locationRelativeProfessionals);
      setRelativeMovies(_mappedMetadata.locationRelativeMovies);

      try {
        const erga = await HttpService.getAxiosClient().get(
          `${process.env.REACT_APP_BACKEND_URL}/ergo?location_id=${id}`
        );
        const _erga = erga.data.data
          .map((ergo: any) => {
            return !ergo['o:is_public']
              ? undefined
              : {
                name: {
                  url: `/search-projects/details/${ergo?.['o:id']}`,
                  label:
                      i18n.language === 'el'
                        ? ergo?.['o:title']
                        : transformGreekToEnglish(ergo?.['o:title']),
                },
                value: ['', ''],
              };
          })
          .filter(Boolean);
        setRelativeProjects(_erga);
      } catch (error) {
        console.error(error);
        setRelativeProjects([]);
      }

      const hiddenRows = [
        'locationAccessSpecialNeeds',
        'locationAccessToHeavyVehicles',
        'locationPlaceRestrictions',
      ];

      setUsefulInfo(
        mapByRefValue(_mappedMetadata, _usefulInfo(_mappedMetadata)).map(
          (x) => {
            return {
              ...x,
              name: t(`${x.name}`),
              value:
                typeof t(x?.value) === 'string' &&
                t(x?.value)
                  ?.replace(/,\s*$/, '')
                  .split(',')
                  .map((value: string) => t(value.trim()))
                  .join(','),
              ...(x.refValue &&
                hiddenRows.includes(x.refValue) && {
                isHiddenRow: !UserService.isAuthenticated(),
              }),
            };
          }
        )
      );

      setContactDetails(
        mapByRefValue(_mappedMetadata, _contactDetails(_mappedMetadata)).map(
          (x) => {
            return {
              ...x,
              name: t(`${x.name}`),
            };
          }
        )
      );

      const { images, videos, thumbnail, attachments } =
        formatMediaDataOmeka(data);

      setMedia([thumbnail, ...images, ...videos]);
      setAttachments(attachments);
      setIsLoading(false);
    } catch (error) {
      console.error(error);
      setErrorMsg(t('Η σελίδα που ζητήσατε δεν βρέθηκε.'));
      setIsLoading(false);
    }
  };

  const onSubmitCommForm = async (values: FormikContextType<FormikValues>) => {
    setIsLoadingContact(true);
    try {
      const resp = await HttpService.getAxiosClient().post(
        `${process.env.REACT_APP_BACKEND_URL}/forms/communication-loc`,
        { ...values, loc_id: Number(id) }
      );
      enqueueSnackbar(
        `${t('Το Αίτημα Επικοινωνίας με τον Ιδιοκτήτη Τοποθεσίας')} ${
          resp.data
        } ${t('στάλθηκε επιτυχώς!')}`,
        { variant: 'success' }
      );
    } catch (error) {
      console.error(error);
      enqueueSnackbar(
        t('Παρουσιάστηκε σφάλμα, παρακαλούμε προσπαθήστε αργότερα.'),
        { variant: 'error' }
      );
    }
    setOpen(false);
    setIsLoadingContact(true);
  };

  const onSubmitFavorite = async (isFavorite: boolean) => {
    try {
      if (!UserService.isAuthenticated()) {
        enqueueSnackbar(
          t(
            'Πρέπει να είστε συνδεδεμένος για να προσθέσετε τοποθεσία στα αγαπημένα σας'
          ),
          { variant: 'error' }
        );
        return;
      }
      const resp = await HttpService.getAxiosClient().patch(
        `${process.env.REACT_APP_BACKEND_URL}/locations/favorites/${id}`,
        { is_favorite: isFavorite }
      );
      if (resp.data.is_favorite === true) {
        enqueueSnackbar(
          `${t('Η τοποθεσία')} ${resp.data.title} ${t(
            'προστέθηκε στα αγαπημένα σας'
          )}`,
          { variant: 'success' }
        );
      } else {
        enqueueSnackbar(
          `${t('Η τοποθεσία')} ${resp.data.title} ${t(
            'αφαιρέθηκε από τα αγαπημένα σας'
          )}`,
          { variant: 'info' }
        );
      }
    } catch (error) {
      setFavoriteError(!favoriteError);
      console.error(error);
      enqueueSnackbar(
        t('Παρουσιάστηκε σφάλμα, παρακαλούμε προσπαθήστε αργότερα.'),
        { variant: 'error' }
      );
    }
  };

  useEffect(() => {
    fetchData();
  }, [favoriteError]);

  const _communicationFields = communicationFields();

  const otherDocs = (
    <Box>
      <Typography mt={3} variant='h3'>
        {t('Άλλα Επισυναπτόμενα Έγγραφα')}
      </Typography>
      <Box mb={3}>
        <Attachments attachments={attachments} />
      </Box>
    </Box>
  );

  if (errorMsg)
    return (
      <Box id='container-details' sx={{ m: 2, height: '600px' }}>
        <EmptyPage errorMsg={errorMsg} />
      </Box>
    );

  return (
    <LocationDetailsSkeleton
      isLoading={isLoading}
      topSlider={true}
      length={3}
      boxesNum={NUMBER_OF_SKELETON_BOXES}
    >
      <Box id='container-details' width='100%'
        mt={3}>
        <Box display='flex' justifyContent='space-between'>
          <Box>
            <Stack flexDirection='row'>
              <Typography
                variant='h2'
                sx={{ mt: 1, color: Theme.labelColor?.main, mr: 2 }}
              >
                {title}
              </Typography>
              {UserService.hasRole([
                Permissions.PermissionExternalLocationFavoritesUpdateMy,
              ]) && is_external
                ? (
                  <IconButton
                    sx={{ mr: 3, ml: 3 }}
                    onClick={() => {
                      setIsFavorite(!isFavorite);
                      onSubmitFavorite(!isFavorite);
                    }}
                  >
                    {isFavorite === true
                      ? (
                        <FavoriteIcon
                          color='error'
                          sx={{
                            border: 'solid 2px #bababa',
                            borderRadius: '50%',
                            padding: 0.5,
                          }}
                        />
                      )
                      : (
                        <FavoriteBorderIcon
                          sx={{
                            border: 'solid 2px #bababa',
                            borderRadius: '50%',
                            padding: 0.5,
                          }}
                        />
                      )}
                  </IconButton>
                )
                : null}
            </Stack>
          </Box>
          <Box display='flex' justifyContent='end'
            gap={2} flexWrap='wrap'>
            {UserService.hasRole([
              Permissions.PermissionExternalLocationApplicationModificationCreateMy,
            ]) &&
              isApplicant && (
              <LinkButton
                link={
                  pathname.split('/').filter(Boolean)[0] === 'admin'
                    ? `/admin/locations/edit/${id}?omeka=true`
                    : `/location/edit/${id}?omeka=true`
                }
                disabled={
                  !(
                    UserService.hasRole([
                      Permissions.PermissionExternalLocationApplicationModificationCreateMy,
                    ]) && isApplicant
                  )
                }
                color='primary'
                variant='contained'
                tooltipTitle={
                  !UserService.isAuthenticated()
                    ? t(
                      'Παρακαλούμε συνδεθείτε για να μπορέσετε να πραγματοποιήσετε αυτή τη λειτουργία'
                    )
                    : !(
                      UserService.hasRole([
                        Permissions.PermissionExternalLocationApplicationModificationCreateMy,
                      ]) && isApplicant
                    )
                      ? t('Δεν έχετε δικαίωμα επεξεργασίας της Τοποθεσίας')
                      : undefined
                }
                sx={{ borderRadius: '24px' }}
              >
                {t('Επεξεργασία Τοποθεσίας')}
              </LinkButton>
            )}
            {!isFormPublishedShootingLoading &&
              availableFormsShooting?.is_published && (
              <LinkButton
                disabled={!UserService.isAuthenticated()}
                color='primary'
                link={`/location/request-form/${id}`}
                variant='contained'
                sx={{ borderRadius: '24px' }}
              >
                {t('Ενδιαφέρον για Γύρισμα') as string}
              </LinkButton>
            )}
            <>
              <Tooltip
                PopperProps={{ disablePortal: true }}
                title={
                  !UserService.isAuthenticated()
                    ? t(
                      'Παρακαλούμε συνδεθείτε για να μπορέσετε να πραγματοποιήσετε αυτή τη λειτουργία'
                    )
                    : ''
                }
              >
                {!isFormPublishedLoading && availableForms.is_published
                  ? (
                    <span>
                      <Button
                        sx={{ borderRadius: '24px' }}
                        disabled={!UserService.isAuthenticated()}
                        variant='contained'
                        color='primary'
                        onClick={handleClickOpen}
                      >
                        {t('Επικοινωνία')}
                      </Button>
                    </span>
                  )
                  : (
                    <></>
                  )}
              </Tooltip>
              <DialogForm
                title={`${t('Επικοινωνία με τον Υπεύθυνο Τοποθεσίας')}`}
                initialValues={initialValues}
                isLoading={isLoadingContact}
                validationSchema={validationSchemaCommForm}
                fields={_communicationFields}
                open={open}
                handleClose={() => setOpen(false)}
                onAccept={onSubmitCommForm}
              />
            </>
          </Box>
        </Box>
        <Divider sx={{ mt: 1, mb: 5 }} />
        <Box m={1}>
          <CarouselContainerForDetails media={media} />
        </Box>
        <Box>
          <>
            {i18n.language !== 'en' && mappedMetadata.description && (
              <Box minWidth={200}>
                <Paper
                  variant='outlined'
                  sx={{
                    height: 'fit-content',
                    borderRadius: 2,
                    boxShadow: 0,
                    backgroundColor: 'transparent',
                  }}
                >
                  <Typography variant='body1' m={3}>
                    {mappedMetadata.description}
                    <Box display='flex' flexWrap='wrap'>
                      {mappedMetadata?.locationKeywords_gr?.length > 0 &&
                        mappedMetadata?.locationKeywords_gr?.map(
                          (item: any) => {
                            return (
                              <Typography
                                key={item}
                                variant='subtitle2'
                                mr={1}
                                mt={3}
                              >
                                <Chip label={item} />
                              </Typography>
                            );
                          }
                        )}
                    </Box>
                    <Box display='flex' flexWrap='wrap'>
                      {mappedMetadata?.locationKeywords?.length > 0 &&
                        mappedMetadata?.locationKeywords?.map((item: any) => {
                          return (
                            <Typography
                              key={item}
                              variant='subtitle2'
                              mr={1}
                              mt={3}
                            >
                              <Chip label={item} />
                            </Typography>
                          );
                        })}
                    </Box>
                  </Typography>
                </Paper>
              </Box>
            )}
            {i18n.language === 'en' && mappedMetadata.description_en && (
              <Box minWidth={200} mt={3}>
                <Paper
                  variant='outlined'
                  sx={{
                    height: 'fit-content',
                    borderRadius: 2,
                    boxShadow: 0,
                    backgroundColor: 'transparent',
                  }}
                >
                  <Typography variant='body1' m={3}>
                    {mappedMetadata.description_en}
                    <Box display='flex' flexWrap='wrap'>
                      {mappedMetadata?.locationKeywords?.length > 0 &&
                        mappedMetadata?.locationKeywords?.map((item: any) => {
                          return (
                            <Typography
                              key={item}
                              variant='subtitle2'
                              mr={1}
                              mt={3}
                            >
                              <Chip label={item} />
                            </Typography>
                          );
                        })}
                    </Box>
                  </Typography>
                </Paper>
              </Box>
            )}
            <DetailsWrapper
              title={t('Στοιχεία Επικοινωνίας')}
              detailsProps={{ rows: contactDetails, removeLines: true }}
            />
          </>
          <DetailsWrapper
            title={t('Χρήσιμες Πληροφορίες')}
            detailsProps={{ rows: usefulInfo, removeLines: true }}
          />
          {UserService.isAuthenticated() && relativeMovies && (
            <DetailsListWrapper
              title={t('Σχετικές Παραγωγές')}
              detailsList={relativeMovies}
            />
          )}
          {UserService.isAuthenticated() &&
            relativeProjects &&
            relativeProjects.length > 0 && (
            <DetailsListWrapper
              title={t('Σχετικές Παραγωγές Έργων')}
              detailsList={relativeProjects}
            />
          )}
          {UserService.isAuthenticated() && relativeProfessionals && (
            <DetailsListWrapper
              title={t(
                'Επαγγελματίες που Δραστηριοποιούνται σε Αυτή την Τοποθεσία'
              )}
              detailsList={relativeProfessionals}
            />
          )}
          {visibility !== VisibilityEnum.Hidden && (
            <>
              {temps.length > 0 && temps[0] && (
                <Box mt={5}>
                  <Paper
                    variant='outlined'
                    sx={{
                      height: 'fit-content',
                      borderRadius: 2,
                      boxShadow: 0,
                      backgroundColor: 'transparent',
                    }}
                  >
                    <Typography m={2} variant='h3'>
                      {t('Ο Καιρός στη Tοποθεσία')}
                    </Typography>
                    <Box
                      display='flex'
                      justifyContent='space-between'
                      sx={{ flexDirection: { xs: 'column', md: 'row' } }}
                    >
                      <Box minWidth='45%' m={2}>
                        <SunIcon
                          viewBox='0 0 60 60'
                          sx={{
                            color: 'grey',
                            height: '120px',
                            width: '120px',
                          }}
                        />
                        <CloudIcon
                          viewBox='0 0 50 50'
                          sx={{
                            color: 'grey',
                            height: '100px',
                            width: '110px',
                          }}
                        />
                      </Box>
                      <Box m={2}>
                        {temps.length > 0 &&
                          temps[0] &&
                          temps.map((obj: any) => {
                            return (
                              <Details
                                renderArraysAsColumns={
                                  matchesXSbreakpoint
                                    ? false
                                    : true
                                }
                                renderFirstRowsAsHeader
                                style={{ backgroundColor: 'black' }}
                                key={obj[0]}
                                rows={formatStrippedDetailsRows(obj)}
                              />
                            );
                          })}
                      </Box>
                    </Box>
                  </Paper>
                </Box>
              )}
              <Box mt={5}>
                <Paper
                  variant='outlined'
                  sx={{
                    minWidth: '200px',
                    height: 'fit-content',
                    borderRadius: 2,
                    boxShadow: 0,
                    backgroundColor: 'transparent',
                  }}
                >
                  <Typography m={2} variant='h3'>
                    {t('Τοποθεσία στον Χάρτη')}
                  </Typography>
                  <Box m={2}>
                    <Details rows={locationInfo} removeLines />
                  </Box>
                  {apiEnabled && !loadingApi
                    ? (
                      <Box minWidth='150px' ml={6}
                        m={2}>
                        <GMapsPlaceMarker
                          visibilityOption={visibility}
                          zoom={12}
                          height='300px'
                          location={location}
                        />
                      </Box>
                    )
                    : null}
                </Paper>
              </Box>

              {locationDistances.length > 0
                ? (
                  <Box mt={5}>
                    <DetailsWrapper
                      title={t('Παρακείμενες Τοποθεσίες')}
                      detailsProps={{
                        rows: locationDistances,
                        removeLines: true,
                      }}
                    />
                  </Box>
                )
                : null}
            </>
          )}
          {/* {attachments && attachments.length > 0 && otherDocs} */}
        </Box>
      </Box>
    </LocationDetailsSkeleton>
  );
}

export default LocationDetails;
